import {
  VuexModule,
  Module,
  Action,
  Mutation,
} from "vuex-class-modules"

import { Modules } from "@/store/modules"
import { store } from "@/store/store"
import {
  AlertContentItem,
  AlertType,
  DefinitionOfDone,
  DefinitionWithItemStatus,
  Item,
  Status,
  StructuredDescription,
  Task,
} from "@/models"
import { TaskActions, taskModule } from "./task.module"
import { Endpoint } from "@/services/endpoints"
import { apiService } from "@/services/api.service"
import { ProjectActions, projectModule } from "./project.module"
import { BaseModule } from "@/models/BaseModule"
import { ItemActions, itemModule } from "./item/item.module"
import { AlertActions, alertModule } from "./alert.module"
import { $t } from "@/plugins/i18n"
import { cutTextByNumberOfSymbols } from "@/helpers/cutTextByNumberOfSymbols"
import cloneDeep from "lodash/cloneDeep"
import { sessionModule } from "./session.module"
import { getAcceptanceCriteriaChecklistState } from "@/helpers/acceptanceCriteriaChecklist"

export enum DefinitionOfDoneActions {
  GET_DEFINITIONS_DEFAULT = "GET_DEFINITIONS_DEFAULT",
  GET_DEFINITIONS_LIST = "GET_DEFINITIONS_LIST",
  GET_DEFINITION_OF_DONE = "GET_DEFINITION_OF_DONE",
  PUT_DEFINITION_OF_DONE = "PUT_DEFINITION_OF_DONE",
  UPDATE_DEFINITION_OF_DONE_LIST = "UPDATE_DEFINITION_OF_DONE_LIST",
  CLEAR_DEFINITION_LIST = "CLEAR_DEFINITION_LIST",
  ALL_DEFINITIONS_LIST = "ALL_DEFINITIONS_LIST",
  RESET_ALL_DEFINITIONS = "RESET_ALL_DEFINITIONS",
}

@Module
class DefinitionOfDoneModule extends BaseModule {
  definitionsList: DefinitionOfDone[] = []
  allDefinitionsList: DefinitionOfDone[] = []

  get taskTypesList() {
    return Task.getTaskTypes(taskModule.tasks)
  }

  get projectId() {
    return projectModule.currentProjectId
  }

  get [`get/${DefinitionOfDoneActions.GET_DEFINITIONS_DEFAULT}`]() {
    return this.getDefaultDefinitionByDefinition()
  }

  get [`get/${DefinitionOfDoneActions.GET_DEFINITIONS_LIST}`]() {
    return this.definitionsList
  }

  get [`get/${DefinitionOfDoneActions.ALL_DEFINITIONS_LIST}`]() {
    return this.allDefinitionsList
  }

  getDefaultDefinitionByDefinition(): DefinitionOfDone[] {
    const item = itemModule.item
    if (!item) return []

    const teamType = item.teamType ?? (item.isSupportCase ? "NONE" : "DEVELOPMENT")
    const itemConfig = sessionModule.itemConfig
    const itemTypeId = item.itemTypeParsed?.id ?? item.itemType

    if (!itemConfig || !itemTypeId) return []

    const selectedConfig = itemConfig[teamType]?.configs?.find(config =>
      config.itemTypes.includes(itemTypeId)
    )

    if (!selectedConfig) return []

    return selectedConfig.definitionsOfDone ?? []
  }

  /**
   * Default definitions can be bound to either legacy `type` or current multi-type `types`.
   */
  hasDefinitionTaskType(definition: DefinitionOfDone) {
    const types = definition.types?.length
      ? definition.types
      : definition.type
        ? [definition.type]
        : []

    return types.some(type => this.taskTypesList?.has(type))
  }

  @Mutation
  setDefinitionList() {
    const availableDefaultDefinitions = this.getDefaultDefinitionByDefinition()
    this.setDefinitionOfDone(availableDefaultDefinitions.map(def => DefinitionOfDone.create({
      ...def,
      enabled: this.hasDefinitionTaskType(def),
    })))
  }

  @Mutation
  setDefinitionOfDone(definition: DefinitionOfDone[]) {
    this.definitionsList = this.syncAcceptanceCriteriaDefinition(definition)
  }

  syncAcceptanceCriteriaDefinition(definition: DefinitionOfDone[]) {
    const state = getAcceptanceCriteriaChecklistState(itemModule.item?.acceptanceCriteria)
    const definitions = definition.filter(def => !def.acceptanceCriteria || state.present)
    if (!state.present) return definitions

    const automaticDefinitionIndex = definitions.findIndex(def => def.acceptanceCriteria)
    const automaticDefinitionSource = definitions[automaticDefinitionIndex]
      ?? this.getDefaultDefinitionByDefinition().find(def => def.acceptanceCriteria)
    if (!automaticDefinitionSource) return definitions

    const automaticDefinition = DefinitionOfDone.create({
      ...automaticDefinitionSource,
      done: state.complete,
      enabled: true,
      manual: false,
    })

    if (automaticDefinitionIndex === -1) return [...definitions, automaticDefinition]

    definitions[automaticDefinitionIndex] = automaticDefinition
    return definitions
  }

  @Mutation
  resetAllDefinitionsList() {
    const availableDefaultDefinitions = this.getDefaultDefinitionByDefinition()
    this.allDefinitionsList = cloneDeep(availableDefaultDefinitions).map(def => {
      const foundDef = this.definitionsList.find(d => d.definition === def.definition)
      if (foundDef) {
        foundDef.enabled = true
        return DefinitionOfDone.create(foundDef)
      }
      return DefinitionOfDone.create(def)
    })
    this.definitionsList.forEach(e => {
      if (!this.allDefinitionsList.find(def => e.definition === def.definition)) {
        e.enabled = true
        this.allDefinitionsList.push(e)
      }
    })
  }

  @Mutation
  setAllDefinitionsList(definition: DefinitionOfDone[]) {
    this.allDefinitionsList = definition
  }

  @Action
  async [DefinitionOfDoneActions.GET_DEFINITIONS_LIST]() {
    this.setDefinitionList()
  }

  @Action
  async [DefinitionOfDoneActions.CLEAR_DEFINITION_LIST]() {
    this.setDefinitionOfDone([])
  }

  @Action
  async [DefinitionOfDoneActions.ALL_DEFINITIONS_LIST](definition: DefinitionOfDone[]) {
    this.setAllDefinitionsList(definition)
  }

  @Action
  async [DefinitionOfDoneActions.RESET_ALL_DEFINITIONS]() {
    this.resetAllDefinitionsList()
  }

  @Action
  async [DefinitionOfDoneActions.UPDATE_DEFINITION_OF_DONE_LIST](definition: DefinitionOfDone[]) {
    this.setDefinitionOfDone(definition)
  }

  @Action
  async [DefinitionOfDoneActions.GET_DEFINITION_OF_DONE]({ projectId, itemId }: { projectId?: string, itemId: string }) {
    const data = await apiService.get<DefinitionWithItemStatus>(Endpoint.DEFINITION_OF_DONE(projectId ?? this.projectId, itemId))
    return this.handleResponse<DefinitionWithItemStatus>(data, (data) => {
      this.handleDefinitionsResponse(data)
    })
  }

  @Action
  async [DefinitionOfDoneActions.PUT_DEFINITION_OF_DONE]({ projectId, item, definitionsOfDone, disableAlert }: { projectId?: string, item: Item, definitionsOfDone: DefinitionOfDone[], disableAlert?: boolean }) {
    const itemId = item?.id ?? ""
    const selectedProject = projectId ? projectId : this.projectId
    const data = await apiService.put<DefinitionWithItemStatus>(Endpoint.DEFINITION_OF_DONE(selectedProject, itemId), definitionsOfDone.map(def => new DefinitionOfDone(def).getJsonObj()))
    return this.handleResponse<DefinitionWithItemStatus>(data, (data) => {
      const itemTitle = cutTextByNumberOfSymbols({
        text: item.title,
        numberOfSymbols: 16,
      })
      const alertContent: AlertContentItem[] = [
        {
          text: "Item ",
          type: "regular",
        },
        {
          text: `${itemTitle} `,
          type: "bold",
        },
        {
          text: $t("alert.updated"),
          type: "regular",
        },
      ]
      if (!disableAlert) {
        alertModule[AlertActions.SHOW_ALERT]({
          content: alertContent,
          type: AlertType.SUCCESS,
          theme: "toast",
          uniqueType: "itemEstimation",
        })
      }
      this.handleDefinitionsResponse(data)
    })
  }

  handleDefinitionsResponse(data) {
    const definitionOfDone = data.definitionsOfDone.map(definition => DefinitionOfDone.create(definition))
    itemModule[ItemActions.UPDATE_ITEM_VALUES]({
      payload: [
        {
          id: "acceptanceCriteria",
          value: data.acceptanceCriteria
            ? new StructuredDescription(data.acceptanceCriteria)
            : itemModule.item?.acceptanceCriteria,
        },
        {
          id: "status",
          value: data.status,
        },
        {
          id: "estimation",
          value: data.estimation,
        },
        {
          id: "statusParsed",
          value: Status.createById({
            id: data.status ?? "NOT_STARTED",
          }),
        },
      ] as any,
      updateClone: true,
    })
    this.setDefinitionOfDone(definitionOfDone)
    return data
  }
}

export const definitionOfDoneModule = new DefinitionOfDoneModule({
  store,
  name: Modules.DEFINITION_OF_DONE,
})
